'use client';

import { SidebarProvider } from '@heidi/design-system/page-shell';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useIntl } from 'react-intl';
import { AgentHomeNavProvider } from '../agent-home-nav-context';
import { navV3Messages } from '../nav-v3.messages';
import { RecentsSection } from '../recents/recents-section';
import { SavedChatsProvider } from '../saved-chats-context';
import { HomePreviewErrorBoundary } from './home-preview-error-boundary';

const KEEP_PREVIEW_SIDEBAR_EXPANDED = () => {};

/**
 * Body of the Home hover preview: a "Recents" heading, then the same
 * merged Recents collection nav-v3's persistent panel renders (search and
 * filter chrome omitted here; the list bounds itself to a collision-safe
 * scroll region via `RecentsSection`'s `compact` variant).
 *
 * Mirrors the persistent panel's provider stack around `RecentsSection`
 * (`agent-home-panel.tsx`'s `SidebarProvider` → `nav-v3.tsx`'s
 * `AgentHomeNavProvider` → `SavedChatsProvider`) — the panel's only provider
 * `RecentsSection` actually depends on beyond these three is none; missing
 * `SavedChatsProvider` here is what let every rendered chat row's
 * `ChatRowActionsMenu` throw `useNavSavedChats must be used within a
 * SavedChatsProvider` straight through to the app's page-level error
 * boundary. `HomePreviewErrorBoundary` is additional containment for any
 * future gap of the same shape.
 */
export const HomePreviewContent = () => {
  const intl = useIntl();

  return (
    <HomePreviewErrorBoundary>
      <div
        className="flex max-h-[var(--radix-popover-content-available-height)] min-h-0 flex-col overflow-hidden"
        data-testid="home-preview-content"
      >
        {/* `px-3` here, matching the rows' own `AGENT_HOME_NAV_ROW` inset
            (`h-9 gap-2.5 px-3`) rather than the compact `SidebarGroup`'s
            zeroed left padding below (see `RecentsSection`'s `isCompact`
            className) — heading text, the date-group label
            (`RecentsDateGroupHeader`'s compact-aware inset) and row icons
            all land on the rows' 92px inset from the popover's own `p-1`
            edge: asserted in `home-preview-content.alignment.test.tsx`'s
            "three-way left-inset alignment" case. The heading sits directly
            against the first date group with no doubled spacing (no `gap-*`
            on this flex column, `p-0` zeroing the compact list's own top
            inset below) — asserted in that same file's "no doubled vertical
            gap" case. */}
        <div className="flex h-8 shrink-0 items-center px-3">
          <TypographyP2
            className="min-w-0 flex-1 truncate text-text-tertiary"
            data-testid="home-preview-recents-heading"
          >
            {intl.formatMessage(navV3Messages.recents)}
          </TypographyP2>
        </div>
        <AgentHomeNavProvider>
          <SidebarProvider
            open
            onOpenChange={KEEP_PREVIEW_SIDEBAR_EXPANDED}
            className="min-h-0 flex-1 bg-transparent"
          >
            <SavedChatsProvider>
              <RecentsSection variant="compact" pollVisits={false} />
            </SavedChatsProvider>
          </SidebarProvider>
        </AgentHomeNavProvider>
      </div>
    </HomePreviewErrorBoundary>
  );
};
